import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  Search,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Edit,
  MoreVertical,
  ArrowUpDown,
  X,
  Loader,
  Save,
  AlertCircle,
  Trash2,
  ArrowLeft,
  Eye,
  MapPin,
  Users,
  Award,
  Link2,
  Upload,
  Mail,
  Phone,
  LayoutGrid,
  List as ListIcon
} from 'lucide-react';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import * as XLSX from 'xlsx';
import { sanitizeInput } from '../../utils/sanitize';

// Common Components
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Badge from '../../components/Common/Badge';

const InstituteChainManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [chains, setChains] = useState([]);
  const [colleges, setColleges] = useState([]); // All colleges for selection
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimer = useRef(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('chainViewMode') || 'list');

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('chainViewMode', mode);
  };

  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingChain, setEditingChain] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    chainName: '',
    email: '',
    contactNumber: '',
    headquarters: '',
    website: '',
    logo: '',
    coordinatorName: '',
    coordinatorDesignation: '',
    coordinatorEmail: '',
    coordinatorMobile: '',
    superior1Name: '',
    superior1Designation: '',
    superior1Email: '',
    superior1Mobile: '',
    superior2Name: '',
    superior2Designation: '',
    superior2Email: '',
    superior2Mobile: '',
    superior3Name: '',
    superior3Designation: '',
    superior3Email: '',
    superior3Mobile: '',
    colleges: [] // Array of college IDs
  });

  const [editFormData, setEditFormData] = useState({
    chainName: '',
    email: '',
    contactNumber: '',
    headquarters: '',
    website: '',
    logo: '',
    coordinatorName: '',
    coordinatorDesignation: '',
    coordinatorEmail: '',
    coordinatorMobile: '',
    superior1Name: '',
    superior1Designation: '',
    superior1Email: '',
    superior1Mobile: '',
    superior2Name: '',
    superior2Designation: '',
    superior2Email: '',
    superior2Mobile: '',
    superior3Name: '',
    superior3Designation: '',
    superior3Email: '',
    superior3Mobile: '',
    colleges: [],
    status: 'active'
  });

  const [formErrors, setFormErrors] = useState({});

  const validateEmail = (email) => {
    return email ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) : true;
  };

  // Debounce search input
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(debounceTimer.current);
  }, [searchTerm]);

  useEffect(() => {
    fetchChains();
  }, [debouncedSearch, statusFilter, currentPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchColleges();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const fetchColleges = async () => {
    try {
      const { data } = await api.get('/colleges', { params: { limit: 9999 } });
      if (data.success) {
        setColleges(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch colleges list:', error);
    }
  };

  const fetchChains = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        sortBy,
        sortOrder,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter })
      };

      const { data } = await api.get('/institute-chains', { params });
      if (data.success) {
        setChains(data.data);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch institute chains:', error);
      toast.error('Failed to fetch institute chains');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const confirmed = window.confirm(`Are you sure you want to change status to ${newStatus}?`);
    if (!confirmed) return;

    try {
      const { data } = await api.put(`/institute-chains/${id}/status`, { status: newStatus });
      if (data.success) {
        toast.success(`Chain status updated to ${newStatus}`);
        fetchChains();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this Institute Chain? This action cannot be undone.');
    if (!confirmed) return;

    try {
      const { data } = await api.delete(`/institute-chains/${id}`);
      if (data.success) {
        toast.success('Institute Chain deleted successfully');
        fetchChains();
      }
    } catch (error) {
      console.error('Failed to delete chain:', error);
      toast.error(error.response?.data?.message || 'Failed to delete Institute Chain');
    }
  };

  const handleExport = () => {
    if (chains.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = chains.map(c => ({
      'Chain Code': sanitizeInput(c.chainCode || 'N/A'),
      'Chain Name': sanitizeInput(c.chainName || 'N/A'),
      'Email': sanitizeInput(c.email),
      'Contact Number': sanitizeInput(c.contactNumber || 'N/A'),
      'Headquarters': sanitizeInput(c.headquarters || 'N/A'),
      'Website': sanitizeInput(c.website || 'N/A'),
      'Coordinator Name': sanitizeInput(c.coordinatorInfo?.name || 'N/A'),
      'Coordinator Email': sanitizeInput(c.coordinatorInfo?.email || 'N/A'),
      'Colleges Count': c.colleges?.length || 0,
      'Status': sanitizeInput(c.status),
      'Created At': new Date(c.createdAt).toLocaleDateString()
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Institute Chains');
    XLSX.writeFile(wb, `Institute_Chains_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Data exported successfully!');
  };

  const handleFileUpload = async (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Logo must be an image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('image', file);

    try {
      const { data } = await api.post('/upload/image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data.success) {
        if (isEdit) {
          setEditFormData(prev => ({ ...prev, logo: data.data.url }));
        } else {
          setFormData(prev => ({ ...prev, logo: data.data.url }));
        }
        toast.success('Logo uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    let errors = {};

    if (!formData.chainName || !formData.email || !formData.website) {
      if (!formData.chainName) errors.chainName = 'Required field';
      if (!formData.email) errors.email = 'Required field';
      if (!formData.website) errors.website = 'Required field';
    }

    if (formData.email && !validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.coordinatorEmail && !validateEmail(formData.coordinatorEmail)) {
      errors.coordinatorEmail = 'Please enter a valid email address';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        chainName: formData.chainName,
        email: formData.email,
        contactNumber: formData.contactNumber,
        headquarters: formData.headquarters,
        website: formData.website,
        logo: formData.logo,
        coordinatorInfo: {
          name: formData.coordinatorName,
          designation: formData.coordinatorDesignation,
          email: formData.coordinatorEmail,
          mobile: formData.coordinatorMobile
        },
        superiorCoordinator1: {
          name: formData.superior1Name,
          designation: formData.superior1Designation,
          email: formData.superior1Email,
          mobile: formData.superior1Mobile
        },
        superiorCoordinator2: {
          name: formData.superior2Name,
          designation: formData.superior2Designation,
          email: formData.superior2Email,
          mobile: formData.superior2Mobile
        },
        superiorCoordinator3: {
          name: formData.superior3Name,
          designation: formData.superior3Designation,
          email: formData.superior3Email,
          mobile: formData.superior3Mobile
        },
        colleges: formData.colleges
      };

      const { data } = await api.post('/institute-chains', payload);
      if (data.success) {
        toast.success(data.message || 'Institute Chain onboarded successfully');
        setIsModalOpen(false);
        setFormData({
          chainName: '',
          email: '',
          contactNumber: '',
          headquarters: '',
          website: '',
          logo: '',
          coordinatorName: '',
          coordinatorDesignation: '',
          coordinatorEmail: '',
          coordinatorMobile: '',
          superior1Name: '',
          superior1Designation: '',
          superior1Email: '',
          superior1Mobile: '',
          superior2Name: '',
          superior2Designation: '',
          superior2Email: '',
          superior2Mobile: '',
          superior3Name: '',
          superior3Designation: '',
          superior3Email: '',
          superior3Mobile: '',
          colleges: []
        });
        setFormErrors({});
        fetchChains();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to onboard Institute Chain');
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (chain) => {
    setEditingChain(chain);
    setEditFormData({
      chainName: chain.chainName,
      email: chain.email,
      contactNumber: chain.contactNumber || '',
      headquarters: chain.headquarters || '',
      website: chain.website || '',
      logo: chain.logo || '',
      coordinatorName: chain.coordinatorInfo?.name || '',
      coordinatorDesignation: chain.coordinatorInfo?.designation || '',
      coordinatorEmail: chain.coordinatorInfo?.email || '',
      coordinatorMobile: chain.coordinatorInfo?.mobile || '',
      superior1Name: chain.superiorCoordinator1?.name || '',
      superior1Designation: chain.superiorCoordinator1?.designation || '',
      superior1Email: chain.superiorCoordinator1?.email || '',
      superior1Mobile: chain.superiorCoordinator1?.mobile || '',
      superior2Name: chain.superiorCoordinator2?.name || '',
      superior2Designation: chain.superiorCoordinator2?.designation || '',
      superior2Email: chain.superiorCoordinator2?.email || '',
      superior2Mobile: chain.superiorCoordinator2?.mobile || '',
      superior3Name: chain.superiorCoordinator3?.name || '',
      superior3Designation: chain.superiorCoordinator3?.designation || '',
      superior3Email: chain.superiorCoordinator3?.email || '',
      superior3Mobile: chain.superiorCoordinator3?.mobile || '',
      colleges: chain.colleges?.map(c => c._id || c) || [],
      status: chain.status || 'active'
    });
    setFormErrors({});
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    let errors = {};

    if (!editFormData.chainName || !editFormData.email || !editFormData.website) {
      if (!editFormData.chainName) errors.chainName = 'Required field';
      if (!editFormData.email) errors.email = 'Required field';
      if (!editFormData.website) errors.website = 'Required field';
    }

    if (editFormData.email && !validateEmail(editFormData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (editFormData.coordinatorEmail && !validateEmail(editFormData.coordinatorEmail)) {
      errors.coordinatorEmail = 'Please enter a valid email address';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        chainName: editFormData.chainName,
        email: editFormData.email,
        contactNumber: editFormData.contactNumber,
        headquarters: editFormData.headquarters,
        website: editFormData.website,
        logo: editFormData.logo,
        coordinatorInfo: {
          name: editFormData.coordinatorName,
          designation: editFormData.coordinatorDesignation,
          email: editFormData.coordinatorEmail,
          mobile: editFormData.coordinatorMobile
        },
        superiorCoordinator1: {
          name: editFormData.superior1Name,
          designation: editFormData.superior1Designation,
          email: editFormData.superior1Email,
          mobile: editFormData.superior1Mobile
        },
        superiorCoordinator2: {
          name: editFormData.superior2Name,
          designation: editFormData.superior2Designation,
          email: editFormData.superior2Email,
          mobile: editFormData.superior2Mobile
        },
        superiorCoordinator3: {
          name: editFormData.superior3Name,
          designation: editFormData.superior3Designation,
          email: editFormData.superior3Email,
          mobile: editFormData.superior3Mobile
        },
        colleges: editFormData.colleges,
        status: editFormData.status
      };

      const { data } = await api.put(`/institute-chains/${editingChain._id}`, payload);
      if (data.success) {
        toast.success('Institute Chain updated successfully');
        setIsEditModalOpen(false);
        fetchChains();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to update Institute Chain');
    } finally {
      setSaving(false);
    }
  };

  const toggleCollegeSelection = (id, isEdit = false) => {
    if (isEdit) {
      setEditFormData(prev => ({
        ...prev,
        colleges: prev.colleges.includes(id)
          ? prev.colleges.filter(cid => cid !== id)
          : [...prev.colleges, id]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        colleges: prev.colleges.includes(id)
          ? prev.colleges.filter(cid => cid !== id)
          : [...prev.colleges, id]
      }));
    }
  };

  // Stats calculation
  const totalChainsCount = chains.length;
  const activeChainsCount = chains.filter(c => c.status === 'active').length;
  const inactiveChainsCount = chains.filter(c => c.status === 'inactive').length;
  const totalCollegesCount = chains.reduce((acc, curr) => acc + (curr.colleges?.length || 0), 0);
  const totalStudentsCount = chains.reduce((acc, curr) => acc + (curr.studentCount || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-[#00152E]/5 p-4 lg:p-6">
      {/* Header */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-gray-200 dark:border-[#1a3884]/35 p-4 mb-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-blue-400 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884] dark:hover:border-[#1a3884] dark:hover:text-white rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div className="p-2 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 rounded-xl shrink-0">
              <Link2 className="w-6 h-6 text-[#1a3884] dark:text-[#4f7dff]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#1a3884] dark:text-[#4f7dff] mb-1">
                Institute Chains
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <Building2 size={14} className="text-[#1a3884] dark:text-[#4f7dff]" />
                <span className="font-medium">
                  Manage groups of affiliated institutions
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="btn-secondary flex items-center gap-2 text-sm px-3 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#00152e] border border-gray-200 dark:border-[#1a3884]/50 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 rounded-xl"
            >
              <Download size={14} className="text-gray-600 dark:text-gray-300" />
              <span>Export</span>
            </button>
            <button
              onClick={fetchChains}
              className="btn-secondary flex items-center gap-2 text-sm px-3 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#00152e] border border-gray-200 dark:border-[#1a3884]/50 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 rounded-xl"
            >
              <RefreshCw size={14} className="text-gray-600 dark:text-gray-300" />
              <span>Refresh</span>
            </button>
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#00152e] p-1 rounded-xl border border-gray-200 dark:border-[#1a3884]/50 mx-2">
              <button
                onClick={() => toggleViewMode('grid')}
                className={`p-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-all duration-300 ${viewMode === 'grid' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              >
                <LayoutGrid size={16} /> <span className="hidden lg:inline">Grid</span>
              </button>
              <button
                onClick={() => toggleViewMode('list')}
                className={`p-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-all duration-300 ${viewMode === 'list' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              >
                <ListIcon size={16} /> <span className="hidden lg:inline">List</span>
              </button>
            </div>

            {currentUser?.role === 'admin' && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 text-sm px-4 py-2.5 bg-[#1a3884] hover:bg-[#132A55] text-white font-semibold rounded-xl transition-all shadow-md shadow-[#1a3884]/15 hover:shadow-lg hover:shadow-[#1a3884]/20 transform hover:-translate-y-0.5"
              >
                <Plus size={14} />
                <span>Onboard Chain</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 animate-fade-in">
        {/* Total Chains */}
        <div className="bg-white dark:bg-[#002147] rounded-xl p-4 border border-gray-100 dark:border-[#1a3884]/35 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate mb-1">Total Chains</p>
              {loading ? (
                <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded animate-pulse w-12 mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalChainsCount}</p>
              )}
            </div>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] dark:from-[#002A5C] dark:to-[#1a3884] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300">
              <Building2 className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        {/* Active Chains */}
        <div className="bg-white dark:bg-[#002147] rounded-xl p-4 border border-gray-100 dark:border-[#1a3884]/35 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate mb-1">Active Chains</p>
              {loading ? (
                <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded animate-pulse w-12 mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{activeChainsCount}</p>
              )}
            </div>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] dark:from-[#002A5C] dark:to-[#1a3884] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        {/* Inactive Chains */}
        <div className="bg-white dark:bg-[#002147] rounded-xl p-4 border border-gray-100 dark:border-[#1a3884]/35 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate mb-1">Inactive Chains</p>
              {loading ? (
                <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded animate-pulse w-12 mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{inactiveChainsCount}</p>
              )}
            </div>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] dark:from-[#002A5C] dark:to-[#1a3884] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300">
              <XCircle className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        {/* Connected Colleges */}
        <div className="bg-white dark:bg-[#002147] rounded-xl p-4 border border-gray-100 dark:border-[#1a3884]/35 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate mb-1">Connected Colleges</p>
              {loading ? (
                <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded animate-pulse w-12 mt-1"></div>
              ) : (
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalCollegesCount}</p>
              )}
            </div>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] dark:from-[#002A5C] dark:to-[#1a3884] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300">
              <Link2 className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Search / Filters */}
      <div className="bg-white dark:bg-[#002147] p-4 rounded-2xl border border-gray-200 dark:border-[#1a3884]/35 shadow-sm mb-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search chains by name, code, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-[#1a3884]/35 bg-white dark:bg-[#00152E] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3884]"
          />
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 dark:border-[#1a3884]/35 bg-white dark:bg-[#00152E] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3884]"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Directory Table */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-[#1a3884]/35 shadow-sm overflow-hidden">
        {viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-[#001f40]/80 border-b border-gray-100 dark:border-[#1a3884]/20">
                <tr>
                  {['Chain Info', 'Coordinator', 'Contact', 'Affiliated Colleges', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-[#1a3884]/20">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader className="w-8 h-8 text-[#1a3884] animate-spin" />
                        <span className="text-sm font-semibold text-slate-500">Loading institute chains...</span>
                      </div>
                    </td>
                  </tr>
                ) : chains.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm font-medium">No institute chains found</p>
                    </td>
                  </tr>
                ) : (
                  chains.map((chain) => (
                    <tr key={chain._id} className="hover:bg-[#1a3884]/5 dark:hover:bg-[#001f40]/40 transition-all">
                      {/* Chain Info */}
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/institute-chain/${chain._id}`)}
                      >
                        <div className="flex items-center gap-3 group">
                          {chain.logo ? (
                            <img src={chain.logo} alt={chain.chainName} className="w-10 h-10 rounded-lg object-cover border border-gray-100" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center text-white font-bold group-hover:from-[#1a3884] group-hover:to-[#00152E]">
                              {chain.chainName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-[#1a3884] dark:group-hover:text-blue-400 transition-colors">{chain.chainName}</p>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                              <span className="font-semibold text-[#1a3884]">{chain.chainCode}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Coordinator */}
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/institute-chain/${chain._id}`)}
                      >
                        {chain.coordinatorInfo?.name ? (
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{chain.coordinatorInfo.name}</p>
                            <p className="text-xs text-slate-500">{chain.coordinatorInfo.designation || 'Coordinator'}</p>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Not Assigned</span>
                        )}
                      </td>

                      {/* Contact */}
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/institute-chain/${chain._id}`)}
                      >
                        <div className="flex flex-col gap-1 text-xs text-gray-800 dark:text-gray-200">
                          {chain.email && (
                            <div className="flex items-center gap-1.5">
                              <Mail size={12} className="text-slate-400 shrink-0" />
                              <span className="truncate max-w-[150px]" title={chain.email}>{chain.email}</span>
                            </div>
                          )}
                          {chain.contactNumber && (
                            <div className="flex items-center gap-1.5">
                              <Phone size={12} className="text-slate-400 shrink-0" />
                              <span>{chain.contactNumber}</span>
                            </div>
                          )}
                          {!chain.email && !chain.contactNumber && (
                            <span className="text-slate-400">N/A</span>
                          )}
                        </div>
                      </td>

                      {/* Affiliated Colleges */}
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/institute-chain/${chain._id}`)}
                      >
                        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#1a3884]/10 text-[#1a3884]">
                          {chain.colleges?.length || 0} Institutions
                        </div>
                      </td>

                      {/* Status */}
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/institute-chain/${chain._id}`)}
                      >
                        <Badge variant={chain.status === 'active' ? 'success' : 'danger'}>
                          {chain.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 text-sm font-medium">
                        <div className="relative flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/institute-chain/${chain._id}`)}
                            className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEditClick(chain)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <select
                            value={chain.status}
                            onChange={(e) => handleStatusChange(chain._id, e.target.value)}
                            className="px-2 py-1 text-xs border border-gray-200 dark:border-[#1a3884]/35 bg-white dark:bg-[#00152E] rounded-lg focus:outline-none"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {loading ? (
              <div className="col-span-full py-12 flex flex-col items-center justify-center gap-3">
                <Loader className="w-8 h-8 text-[#1a3884] animate-spin" />
                <span className="text-sm font-semibold text-slate-500">Loading institute chains...</span>
              </div>
            ) : chains.length === 0 ? (
              <div className="col-span-full py-12 flex flex-col items-center justify-center text-gray-500">
                <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                <p className="text-sm font-medium">No institute chains found</p>
              </div>
            ) : (
              chains.map((chain) => (
                <div key={chain._id} className="bg-white dark:bg-[#002147] border border-gray-100 dark:border-[#1a3884]/30 rounded-2xl p-4 shadow-sm hover:shadow-lg transition-all group flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {chain.logo ? (
                        <img src={chain.logo} alt={chain.chainName} className="w-12 h-12 rounded-xl object-cover border border-gray-100 dark:border-gray-700" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center text-white font-bold text-lg shadow-inner">
                          {chain.chainName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-white line-clamp-1 group-hover:text-[#1a3884] dark:group-hover:text-blue-400 transition-colors cursor-pointer" onClick={() => navigate(`/institute-chain/${chain._id}`)}>
                          {chain.chainName}
                        </h3>
                        <p className="text-xs text-gray-500 font-medium mt-0.5">{chain.chainCode}</p>
                      </div>
                    </div>
                    <Badge variant={chain.status === 'active' ? 'success' : 'danger'}>
                      {chain.status === 'active' ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  <div className="space-y-2 mt-auto">
                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <Users size={14} className="text-[#1a3884]" />
                      <span className="truncate">{chain.coordinatorInfo?.name || 'No Coordinator'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <Mail size={14} className="text-[#1a3884]" />
                      <span className="truncate">{chain.email || 'No Email'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <Phone size={14} className="text-[#1a3884]" />
                      <span className="truncate">{chain.contactNumber || 'No Phone'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <Link2 size={14} className="text-[#1a3884]" />
                      <span className="truncate font-semibold text-[#1a3884] bg-[#1a3884]/10 px-2 py-0.5 rounded-full">{chain.colleges?.length || 0} Affiliated Institutions</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-[#1a3884]/30 flex items-center justify-between">
                    <button
                      onClick={() => navigate(`/institute-chain/${chain._id}`)}
                      className="text-xs font-semibold text-[#1a3884] dark:text-blue-400 hover:underline flex items-center gap-1"
                    >
                      View Details <ArrowLeft size={12} className="rotate-180" />
                    </button>
                    <div className="flex gap-1">
                      <button onClick={() => handleEditClick(chain)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Edit size={14} /></button>
                      <button onClick={() => handleDelete(chain._id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-[#1a3884]/20 flex items-center justify-between bg-gray-50/50 dark:bg-[#001f40]/20">
            <div className="text-xs font-semibold text-slate-500">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>


      {/* Onboard Chain Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 lg:left-64 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#002147] rounded-2xl w-full max-w-3xl shadow-2xl border border-gray-200 dark:border-[#1a3884]/35 animate-scale-up flex flex-col max-h-[92vh]">
            <form onSubmit={handleCreateSubmit} className="flex flex-col max-h-[92vh]">
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-100 dark:border-[#1a3884]/20 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 rounded-xl">
                    <Link2 className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1a3884] dark:text-[#4f7dff]">Onboard Institute Chain</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Create a new group of affiliated institutions</p>
                  </div>
                </div>
                <button type="button" onClick={() => setIsModalOpen(false)} className="p-2 text-slate-400 hover:text-[#1a3884] dark:hover:text-[#4f7dff] hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 rounded-xl transition-all">
                  <X size={20} />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="pro-form p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar">
                {/* Section: Chain Details */}
                <section className="bg-gray-50/60 dark:bg-[#001E3D]/30 rounded-2xl p-5 border border-gray-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Chain Details</h4>
                  </div>

                  {/* Logo */}
                  <div className="flex items-center gap-4 mb-5">
                    {formData.logo ? (
                      <img src={getFileUrl(formData.logo)} alt="Preview" className="w-16 h-16 rounded-xl object-contain bg-white border border-gray-200 dark:border-[#1a3884]/40 p-1 shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-white dark:bg-[#00152E] flex items-center justify-center text-gray-400 dark:text-gray-500 border border-dashed border-gray-300 dark:border-[#1a3884]/35 text-[10px] font-semibold shrink-0">
                        No Logo
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        id="onboard-logo-upload"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, false)}
                        className="hidden"
                        disabled={uploading}
                      />
                      <label
                        htmlFor="onboard-logo-upload"
                        className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-[#1a3884]/30 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Upload size={14} />
                        {uploading ? 'Uploading...' : formData.logo ? 'Change Logo' : 'Upload Logo'}
                      </label>
                      <p className="text-[10px] text-slate-400 mt-1">Recommended: Square image, max 5MB</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Input
                        label="Chain Name *"
                        placeholder="e.g. Smaart Group of Institutions"
                        value={formData.chainName}
                        onChange={(e) => setFormData(prev => ({ ...prev, chainName: e.target.value }))}
                        maxLength={100}
                      />
                    </div>
                    <Input
                      label="Website URL *"
                      type="url"
                      placeholder="e.g. https://www.smaartgroup.com"
                      value={formData.website}
                      onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                      maxLength={100}
                    />
                    <Input
                      label="Official E-mail *"
                      type="email"
                      placeholder="e.g. info@smaartgroup.com"
                      value={formData.email}
                      onChange={(e) => { setFormData(prev => ({ ...prev, email: e.target.value })); setFormErrors(prev => ({ ...prev, email: '' })); }}
                      maxLength={100}
                      error={formErrors.email}
                    />
                    <Input
                      label="Contact Number"
                      placeholder="e.g. +91 9876543210"
                      type="tel"
                      value={formData.contactNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactNumber: e.target.value.replace(/\D/g, '').slice(0, 15) }))}
                    />
                    <Input
                      label="Headquarters Location"
                      placeholder="e.g. Bangalore, India"
                      value={formData.headquarters}
                      onChange={(e) => setFormData(prev => ({ ...prev, headquarters: e.target.value }))}
                      maxLength={100}
                    />
                  </div>
                </section>

                {/* Section: Primary Coordinator */}
                <section className="bg-gray-50/60 dark:bg-[#001E3D]/30 rounded-2xl p-5 border border-gray-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Primary Coordinator</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Coordinator Name *"
                      placeholder="e.g. Rajesh Kumar"
                      value={formData.coordinatorName}
                      onChange={(e) => setFormData(prev => ({ ...prev, coordinatorName: e.target.value }))}
                      maxLength={50}
                    />
                    <Input
                      label="Designation *"
                      placeholder="e.g. Managing Director"
                      value={formData.coordinatorDesignation}
                      onChange={(e) => setFormData(prev => ({ ...prev, coordinatorDesignation: e.target.value }))}
                      maxLength={50}
                    />
                    <Input
                      label="Coordinator Email *"
                      type="email"
                      placeholder="e.g. rajesh@smaartgroup.com"
                      value={formData.coordinatorEmail}
                      onChange={(e) => { setFormData(prev => ({ ...prev, coordinatorEmail: e.target.value })); setFormErrors(prev => ({ ...prev, coordinatorEmail: '' })); }}
                      maxLength={100}
                      error={formErrors.coordinatorEmail}
                    />
                    <Input
                      label="Coordinator Mobile"
                      type="tel"
                      placeholder="e.g. 9876543211"
                      value={formData.coordinatorMobile}
                      onChange={(e) => setFormData(prev => ({ ...prev, coordinatorMobile: e.target.value.replace(/\D/g, '').slice(0, 15) }))}
                    />
                  </div>
                </section>

                {/* Section: Affiliated Institutions */}
                <section className="bg-gray-50/60 dark:bg-[#001E3D]/30 rounded-2xl p-5 border border-gray-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Affiliated Institutions</h4>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/10 dark:bg-[#1a3884]/25 px-2 py-0.5 rounded-full">{formData.colleges.length} selected</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Select the institutions that belong to this chain.</p>
                  {colleges.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No institutions available.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1 custom-scrollbar">
                      {colleges.map((c) => {
                        const checked = formData.colleges.includes(c._id);
                        return (
                          <label key={c._id} className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-all ${checked ? 'border-[#1a3884] bg-[#1a3884]/5 dark:bg-[#1a3884]/20' : 'border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/10'}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleCollegeSelection(c._id, false)} className="w-4 h-4 accent-[#1a3884] shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">{c.collegeName}</p>
                              <p className="text-[10.5px] text-gray-500 dark:text-gray-400 truncate">{c.collegeCode}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* Section: Superior Coordinators (Optional) */}
                <section className="bg-gray-50/60 dark:bg-[#001E3D]/30 rounded-2xl p-5 border border-gray-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Superior Coordinators</h4>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 bg-gray-200/70 dark:bg-[#1a3884]/30 px-2 py-0.5 rounded-full">Optional</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Add up to three superior coordinators for this chain.</p>

                  <div className="space-y-5">
                    <div>
                      <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Coordinator #1</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Superior Name"
                          placeholder="e.g. Anil Kumar"
                          value={formData.superior1Name}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior1Name: e.target.value }))}
                        />
                        <Input
                          label="Designation"
                          placeholder="e.g. Director"
                          value={formData.superior1Designation}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior1Designation: e.target.value }))}
                        />
                        <Input
                          label="Superior Email"
                          type="email"
                          placeholder="e.g. anil@smaartgroup.com"
                          value={formData.superior1Email}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior1Email: e.target.value }))}
                        />
                        <Input
                          label="Superior Mobile"
                          placeholder="e.g. 9876543212"
                          value={formData.superior1Mobile}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior1Mobile: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="border-t border-gray-200/70 dark:border-[#1a3884]/20 pt-4">
                      <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Coordinator #2</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Superior Name"
                          placeholder="e.g. Sunil Kumar"
                          value={formData.superior2Name}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior2Name: e.target.value }))}
                        />
                        <Input
                          label="Designation"
                          placeholder="e.g. Vice President"
                          value={formData.superior2Designation}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior2Designation: e.target.value }))}
                        />
                        <Input
                          label="Superior Email"
                          type="email"
                          placeholder="e.g. sunil@smaartgroup.com"
                          value={formData.superior2Email}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior2Email: e.target.value }))}
                        />
                        <Input
                          label="Superior Mobile"
                          placeholder="e.g. 9876543213"
                          value={formData.superior2Mobile}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior2Mobile: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="border-t border-gray-200/70 dark:border-[#1a3884]/20 pt-4">
                      <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Coordinator #3</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Superior Name"
                          placeholder="e.g. Ramesh Chandra"
                          value={formData.superior3Name}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior3Name: e.target.value }))}
                        />
                        <Input
                          label="Designation"
                          placeholder="e.g. Chairman"
                          value={formData.superior3Designation}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior3Designation: e.target.value }))}
                        />
                        <Input
                          label="Superior Email"
                          type="email"
                          placeholder="e.g. ramesh@smaartgroup.com"
                          value={formData.superior3Email}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior3Email: e.target.value }))}
                        />
                        <Input
                          label="Superior Mobile"
                          placeholder="e.g. 9876543214"
                          value={formData.superior3Mobile}
                          onChange={(e) => setFormData(prev => ({ ...prev, superior3Mobile: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              {/* Sticky Footer */}
              <div className="px-6 py-4 border-t border-gray-100 dark:border-[#1a3884]/20 flex justify-end gap-3 shrink-0 bg-white dark:bg-[#002147] rounded-b-2xl">
                <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Onboard Chain
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Chain Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 lg:left-64 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#002147] rounded-2xl w-full max-w-3xl shadow-2xl border border-gray-200 dark:border-[#1a3884]/35 animate-scale-up flex flex-col max-h-[92vh]">
            <form onSubmit={handleEditSubmit} className="flex flex-col max-h-[92vh]">
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-100 dark:border-[#1a3884]/20 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 rounded-xl">
                    <Link2 className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1a3884] dark:text-[#4f7dff]">Edit Institute Chain</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Update the details of this institution group</p>
                  </div>
                </div>
                <button type="button" onClick={() => setIsEditModalOpen(false)} className="p-2 text-slate-400 hover:text-[#1a3884] dark:hover:text-[#4f7dff] hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 rounded-xl transition-all">
                  <X size={20} />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="pro-form p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar">
                {/* Section: Chain Details */}
                <section className="bg-gray-50/60 dark:bg-[#001E3D]/30 rounded-2xl p-5 border border-gray-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Chain Details</h4>
                  </div>

                  {/* Logo */}
                  <div className="flex items-center gap-4 mb-5">
                    {editFormData.logo ? (
                      <img src={getFileUrl(editFormData.logo)} alt="Preview" className="w-16 h-16 rounded-xl object-contain bg-white border border-gray-200 dark:border-[#1a3884]/40 p-1 shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-white dark:bg-[#00152E] flex items-center justify-center text-gray-400 dark:text-gray-500 border border-dashed border-gray-300 dark:border-[#1a3884]/35 text-[10px] font-semibold shrink-0">
                        No Logo
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        id="edit-logo-upload"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, true)}
                        className="hidden"
                        disabled={uploading}
                      />
                      <label
                        htmlFor="edit-logo-upload"
                        className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-[#1a3884]/30 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-xs font-semibold text-gray-700 dark:text-gray-300 transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Upload size={14} />
                        {uploading ? 'Uploading...' : editFormData.logo ? 'Change Logo' : 'Upload Logo'}
                      </label>
                      <p className="text-[10px] text-slate-400 mt-1">Recommended: Square image, max 5MB</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Input
                        label="Chain Name *"
                        placeholder="e.g. Smaart Group of Institutions"
                        value={editFormData.chainName}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, chainName: e.target.value }))}
                        maxLength={100}
                      />
                    </div>
                    <Input
                      label="Website URL *"
                      type="url"
                      placeholder="e.g. https://www.smaartgroup.com"
                      value={editFormData.website}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, website: e.target.value }))}
                      maxLength={100}
                    />
                    <Input
                      label="Official E-mail *"
                      type="email"
                      placeholder="e.g. info@smaartgroup.com"
                      value={editFormData.email}
                      onChange={(e) => { setEditFormData(prev => ({ ...prev, email: e.target.value })); setFormErrors(prev => ({ ...prev, email: '' })); }}
                      maxLength={100}
                      error={formErrors.email}
                    />
                    <Input
                      label="Contact Number"
                      type="tel"
                      placeholder="e.g. +91 9876543210"
                      value={editFormData.contactNumber}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, contactNumber: e.target.value.replace(/\D/g, '').slice(0, 15) }))}
                    />
                    <Input
                      label="Headquarters Location"
                      placeholder="e.g. Bangalore, India"
                      value={editFormData.headquarters}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, headquarters: e.target.value }))}
                      maxLength={100}
                    />
                    <Select
                      label="Status"
                      value={editFormData.status}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
                      options={[
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' }
                      ]}
                    />
                  </div>
                </section>

                {/* Section: Primary Coordinator */}
                <section className="bg-gray-50/60 dark:bg-[#001E3D]/30 rounded-2xl p-5 border border-gray-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Primary Coordinator</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Coordinator Name *"
                      placeholder="e.g. Rajesh Kumar"
                      value={editFormData.coordinatorName}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, coordinatorName: e.target.value }))}
                      maxLength={50}
                    />
                    <Input
                      label="Designation *"
                      placeholder="e.g. Managing Director"
                      value={editFormData.coordinatorDesignation}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, coordinatorDesignation: e.target.value }))}
                      maxLength={50}
                    />
                    <Input
                      label="Coordinator Email *"
                      type="email"
                      placeholder="e.g. rajesh@smaartgroup.com"
                      value={editFormData.coordinatorEmail}
                      onChange={(e) => { setEditFormData(prev => ({ ...prev, coordinatorEmail: e.target.value })); setFormErrors(prev => ({ ...prev, coordinatorEmail: '' })); }}
                      maxLength={100}
                      error={formErrors.coordinatorEmail}
                    />
                    <Input
                      label="Coordinator Mobile"
                      type="tel"
                      placeholder="e.g. 9876543211"
                      value={editFormData.coordinatorMobile}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, coordinatorMobile: e.target.value.replace(/\D/g, '').slice(0, 15) }))}
                    />
                  </div>
                </section>

                {/* Section: Affiliated Institutions */}
                <section className="bg-gray-50/60 dark:bg-[#001E3D]/30 rounded-2xl p-5 border border-gray-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Affiliated Institutions</h4>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/10 dark:bg-[#1a3884]/25 px-2 py-0.5 rounded-full">{editFormData.colleges.length} selected</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Select the institutions that belong to this chain.</p>
                  {colleges.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">No institutions available.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1 custom-scrollbar">
                      {colleges.map((c) => {
                        const checked = editFormData.colleges.includes(c._id);
                        return (
                          <label key={c._id} className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-all ${checked ? 'border-[#1a3884] bg-[#1a3884]/5 dark:bg-[#1a3884]/20' : 'border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/10'}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleCollegeSelection(c._id, true)} className="w-4 h-4 accent-[#1a3884]" />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{c.collegeName}</p>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{c.collegeCode}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* Section: Superior Coordinators (Optional) */}
                <section className="bg-gray-50/60 dark:bg-[#001E3D]/30 rounded-2xl p-5 border border-gray-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="w-4 h-4 text-[#1a3884] dark:text-[#4f7dff]" />
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">Superior Coordinators</h4>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 bg-gray-200/70 dark:bg-[#1a3884]/30 px-2 py-0.5 rounded-full">Optional</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Add up to three superior coordinators for this chain.</p>

                  <div className="space-y-5">
                    <div>
                      <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Coordinator #1</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Superior Name"
                          placeholder="e.g. Anil Kumar"
                          value={editFormData.superior1Name}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior1Name: e.target.value }))}
                        />
                        <Input
                          label="Designation"
                          placeholder="e.g. Director"
                          value={editFormData.superior1Designation}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior1Designation: e.target.value }))}
                        />
                        <Input
                          label="Superior Email"
                          type="email"
                          placeholder="e.g. anil@smaartgroup.com"
                          value={editFormData.superior1Email}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior1Email: e.target.value }))}
                        />
                        <Input
                          label="Superior Mobile"
                          placeholder="e.g. 9876543212"
                          value={editFormData.superior1Mobile}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior1Mobile: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="border-t border-gray-200/70 dark:border-[#1a3884]/20 pt-4">
                      <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Coordinator #2</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Superior Name"
                          placeholder="e.g. Sunil Kumar"
                          value={editFormData.superior2Name}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior2Name: e.target.value }))}
                        />
                        <Input
                          label="Designation"
                          placeholder="e.g. Vice President"
                          value={editFormData.superior2Designation}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior2Designation: e.target.value }))}
                        />
                        <Input
                          label="Superior Email"
                          type="email"
                          placeholder="e.g. sunil@smaartgroup.com"
                          value={editFormData.superior2Email}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior2Email: e.target.value }))}
                        />
                        <Input
                          label="Superior Mobile"
                          placeholder="e.g. 9876543213"
                          value={editFormData.superior2Mobile}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior2Mobile: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="border-t border-gray-200/70 dark:border-[#1a3884]/20 pt-4">
                      <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Coordinator #3</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Superior Name"
                          placeholder="e.g. Ramesh Chandra"
                          value={editFormData.superior3Name}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior3Name: e.target.value }))}
                        />
                        <Input
                          label="Designation"
                          placeholder="e.g. Chairman"
                          value={editFormData.superior3Designation}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior3Designation: e.target.value }))}
                        />
                        <Input
                          label="Superior Email"
                          type="email"
                          placeholder="e.g. ramesh@smaartgroup.com"
                          value={editFormData.superior3Email}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior3Email: e.target.value }))}
                        />
                        <Input
                          label="Superior Mobile"
                          placeholder="e.g. 9876543214"
                          value={editFormData.superior3Mobile}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, superior3Mobile: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              {/* Sticky Footer */}
              <div className="px-6 py-4 border-t border-gray-100 dark:border-[#1a3884]/20 flex justify-end gap-3 shrink-0 bg-white dark:bg-[#002147] rounded-b-2xl">
                <Button variant="outline" type="button" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstituteChainManagement;
